import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  PlanStatus,
  CreatePlan,
  PlanResponse,
  UpdatePlan
} from '../common/contracts';
import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

interface PlansFilters {
  pageSize: number;
}

const DEFAULT_PLANS_FILTERS: PlansFilters = {
  pageSize: 10
};

export default function usePlans(
  plansFilters: PlansFilters = DEFAULT_PLANS_FILTERS,
  planId?: string
) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { publisherId } = useParams();

  const getPlans = useQuery<PlanResponse[]>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId],
    queryFn: async () => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}?pageSize=${plansFilters.pageSize}`;

      return await axios.get(
        plansPath,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    }
  });

  const getPlan = useQuery<PlanResponse>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.SUBSCRIPTION_PLAN, publisherId, planId],
    enabled: !!planId,
    queryFn: async () => {
      const planPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}`;

      return await axios.get(
        planPath,
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    }
  });

  const createPlan = useMutation<PlanResponse, Error, CreatePlan>({
    mutationFn: async (planData: CreatePlan): Promise<PlanResponse> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}`;

      const response = await axios.post(
        plansPath,
        planData,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  const updatePlan = useMutation<
    PlanResponse,
    Error,
    { planId: string; planData: UpdatePlan }
  >({
    mutationFn: async ({ planId, planData }): Promise<PlanResponse> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}`;
      const response = await axios.patch(plansPath, planData);

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  const archivePlan = useMutation<PlanResponse, Error, string>({
    mutationFn: async (planId: string): Promise<PlanResponse> => {
      const plansPath = `${getApiRouteOrMock(EApiRoutes.PLANS)}/plan/${planId}/archive`;

      const response = await axios.patch(plansPath, {
        status: PlanStatus.ARCHIVED
      });

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.SUBSCRIPTION_PLANS, publisherId]
      });

      return response.data;
    }
  });

  return {
    getPlans,
    getPlan,
    createPlan,
    updatePlan,
    archivePlan
  };
}
